'use client';

import React from 'react';
import { Drawer, DrawerContent, LucideIcon } from '@skin-seoul/design-system';
import type { Currency } from '@skin-seoul/service';
import { CartDiscoveryNavigation, useCartDiscovery } from './components/CartDiscoveryNavigation';
import { CartAddToBagProvider } from './components/CartDiscoveryCard';
import { CartMutationLoader } from './components/CartLoaderBackdrop';
import ShoppingBagDrawerContent from './ShoppingBagDrawerContent';
import ShoppingBagSkeleton from './ShoppingBagSkeleton';
import QueryBoundary from '../../utils/react/QueryBoundary';
import { useCartDrawer } from '../../context/CartDrawerContext';
import { useCartVisitAnalytics } from './components/useCartVisitAnalytics';

const SHOPPING_BAG_DRAWER_TEST_ID = 'cart-drawer-content';

type ShoppingBagDrawerProps = {
  currency: Currency;
  children?: React.ReactNode;
  onClickContinueShopping: () => void;
  /**
   * Optional callback triggered when checkout button is clicked
   * Parent can use this for analytics tracking
   */
  onCheckoutClick?: () => void;
  ImageComponent?: React.ComponentType<{ src: string; alt: string; width: number; height: number }>;
  /**
   * Custom Link component to use for navigation.
   * Useful for injecting Next.js Link in webapp or other routing solutions.
   * @default Link from @skin-seoul/design-system
   */
  linkComponent?: React.ElementType;
};

const ShoppingBagDrawer = ({
  currency,
  children,
  onClickContinueShopping,
  onCheckoutClick,
  ImageComponent,
  linkComponent,
}: ShoppingBagDrawerProps) => {
  const { isOpen, onSetCartState } = useCartDrawer();
  const trackCheckoutDeparture = useCartVisitAnalytics(isOpen, currency);

  return (
    <Drawer direction="right" onOpenChange={(open) => onSetCartState(open)} open={isOpen}>
      <DrawerContent
        accessibleTitle="Shopping bag"
        // Vaul's persistent transform promotion softens the settled drawer at DPR1.
        // Active transforms still receive the browser's normal compositing behavior.
        style={{ willChange: 'auto' }}
        className="data-[vaul-drawer-direction=right]:w-full mobile:data-[vaul-drawer-direction=right]:max-w-[450px] items-center justify-center bg-white max-h-[100dvh]"
        tabIndex={isOpen ? undefined : -1}
        aria-hidden={!isOpen}
        id="shopping-bag-drawer-content"
        data-testid={SHOPPING_BAG_DRAWER_TEST_ID}
      >
        {children}
        <QueryBoundary
          loadingFallback={
            <>
              <CartDismissButton />
              <ShoppingBagSkeleton />
            </>
          }
        >
          <CartAddToBagProvider currency={currency}>
            <CartDiscoveryNavigation currency={currency}>
              <CartDismissButton />
              <ShoppingBagDrawerContent
                currency={currency}
                onClickContinueShopping={onClickContinueShopping}
                onCheckoutClick={() => {
                  trackCheckoutDeparture();
                  onCheckoutClick?.();
                }}
                ImageComponent={ImageComponent}
                linkComponent={linkComponent}
              />
            </CartDiscoveryNavigation>
          </CartAddToBagProvider>
        </QueryBoundary>
        {isOpen && <CartMutationLoader />}
      </DrawerContent>
    </Drawer>
  );
};

export default ShoppingBagDrawer;

function CartDismissButton() {
  const { isOpen, onCartClose } = useCartDrawer();
  const { isDetailOpen } = useCartDiscovery();
  if (isDetailOpen) {
    return null;
  }
  return (
    <button
      aria-label="Close cart"
      className="inline-flex items-center justify-center absolute top-3 right-3 z-banner cursor-pointer bg-transparent border-none hover:bg-transparent"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onCartClose();
      }}
      tabIndex={isOpen ? 0 : -1}
    >
      <LucideIcon name="X" className="text-generic-100 w-6 h-6" />
    </button>
  );
}
